import { MARKETING_MODULES, type ModuleStageId } from './marketingModules';

/**
 * The /how-it-works registry — one chapter per step a real job passes through,
 * with the product recordings that belong to it.
 *
 * Kept in a plain (non-"use client") module for the same reason as
 * marketingTokens.ts: the Server Component at src/app/how-it-works/page.tsx
 * reads these values to emit VideoObject / ItemList JSON-LD, and a "use client"
 * import would hand it a client-reference proxy instead of the real data.
 *
 * The chapter spine deliberately reuses MODULE_STAGES from ./marketingModules —
 * the same six stages the /modules hub is laid out by, in the same builder's
 * own words — rather than inventing a second vocabulary for the same lifecycle.
 * Two stages carry enough recordings to split in two ('site' into setup and
 * daily running, 'material' into material and contractors), so eight chapters
 * map onto six stages via the `stage` join key.
 *
 * Every id, thumbnail and `published` date was verified against the live
 * channel. `published` is a REAL upload date, never a guess — it feeds
 * schema.org uploadDate, and an invented date there is a lie to Google. If a
 * row ever loses its real date, drop its VideoObject rather than fabricate one.
 *
 * Nothing here is fetched at build time. A marketing page must not make
 * `next build` depend on YouTube being reachable.
 */

export interface Video {
  /** YouTube video id — thumbnails resolve through thumbnailUrl() below. */
  id: string;
  title: string;
  /** One operational line. What the viewer will actually see happen. */
  blurb: string;
  /** Real upload date, YYYY-MM-DD. */
  published: string;
}

/**
 * Background band for a chapter. Bands, not cards.
 *
 * Tone is the ONLY thing that varies between chapters. There is deliberately no
 * per-chapter layout: every recording on this page is a product tutorial, so
 * every recording renders at the same size, in the same one-up / two-up grid.
 * Featured-versus-supporting sizing said some tutorials mattered less, which is
 * not true and made the page read as a video gallery.
 */
export type ChapterTone = 'light' | 'alt' | 'dark';

export interface Chapter {
  /** Two-digit display number. */
  n: string;
  /** Anchor id — the chapter rail links to #<slug>. */
  slug: string;
  label: string;
  /** One word for the sticky workflow rail. */
  short: string;
  tone: ChapterTone;
  /** Join key back to MODULE_STAGES. */
  stage: ModuleStageId;
  lede: string;
  /** "What you end up with" — the state of the business after this chapter. */
  outcome: string;
  /** MARKETING_MODULES slugs this chapter deep-links to. */
  modules: string[];
  videos: Video[];
}

export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@BuilderSiteNow';

export const HOW_IT_WORKS_CHAPTERS: Chapter[] = [
  {
    n: '01',
    slug: 'enquiry',
    label: 'An enquiry comes in',
    short: 'Enquiry',
    tone: 'light',
    stage: 'enquiry',
    lede: 'Someone calls about a plot. Today that is a name in a notebook and a follow-up that happens if somebody remembers. Here it lands in a pipeline that knows who owns it and what happens next. This is the full client walkthrough — if you watch one thing on this page, watch this one.',
    outcome: 'A captured lead with an owner, a source and a dated next action.',
    modules: ['leads'],
    videos: [
      { id: 'Ye0NZRbd05w', title: 'Lead Module — Complete Client Demo', blurb: 'The end-to-end walkthrough: capturing an enquiry, working the pipeline and moving it forward.', published: '2026-09-13' },
    ],
  },
  {
    n: '02',
    slug: 'price',
    label: 'You put a price on it',
    short: 'Estimate',
    tone: 'alt',
    stage: 'quotation',
    lede: 'However you quote — a bill of quantities at your own rates, or a package at a rate per square foot — the number you send the client is the number that becomes the budget when you win it.',
    outcome: 'A priced estimate on your letterhead, and the settings behind it set once.',
    modules: ['estimates', 'packages'],
    videos: [
      { id: 'LXqoz0rwEK0', title: 'Create an Estimate', blurb: 'Building a client estimate from your rates, through to the document you send.', published: '2026-09-14' },
      { id: '4GV54UDuuWQ', title: 'Estimate Settings & Configuration', blurb: 'Contract models, rate drivers and milestone templates — configured once, used on every quote.', published: '2026-09-14' },
    ],
  },
  {
    n: '03',
    slug: 'site',
    label: 'The job becomes a site',
    short: 'Site',
    tone: 'light',
    stage: 'site',
    lede: 'Nothing is re-typed. The estimate you won carries its budget, its stage payment plan and its scope across — and the people who will run it are posted on day one.',
    outcome: 'A live site with its budget, payment plan and team already in place.',
    modules: ['sites', 'schedule'],
    videos: [
      { id: 'AEFjem-xe_c', title: 'Create a New Site', blurb: 'Opening a project: identity, dates, area, budget and the owner split.', published: '2026-09-13' },
      { id: 'U6FlvjvE2EU', title: 'Site Overview & Project Dashboard', blurb: 'What the site page tells you at a glance — and where each number comes from.', published: '2026-09-13' },
      { id: 'h8CLBulO3FE', title: 'Manage Site Team & Staff', blurb: 'Posting supervisors and crew to a site, and what each of them can then see.', published: '2026-09-13' },
    ],
  },
  {
    n: '04',
    slug: 'daily',
    label: 'The site runs day to day',
    short: 'Execution',
    tone: 'dark',
    stage: 'site',
    lede: 'Progress, the diary, notes and the muster — the four things that happen every day at the gate, marked from a phone instead of reconstructed from memory on Saturday.',
    outcome: 'A dated record of what was done, by whom, on every working day.',
    modules: ['sites', 'attendance'],
    videos: [
      { id: 'RWN9dOCtpOI', title: 'Track Construction Progress', blurb: 'Ticking off activities so the percentage is computed from work done, not typed in.', published: '2026-09-13' },
      { id: 'vgdHuOxmp9M', title: 'Construction Site Diary', blurb: 'The day book: weather, labour on site, what moved and what stopped.', published: '2026-09-13' },
      { id: 'VAwxFXSthMQ', title: 'Site Notes & Project Updates', blurb: 'The digital site notebook — instructions, observations and updates against the site.', published: '2026-09-13' },
      { id: 'DnbcM_kkFco', title: 'Site Attendance', blurb: 'Marking the daily muster at the site, including leave, holidays and loss of pay.', published: '2026-09-13' },
    ],
  },
  {
    n: '05',
    slug: 'material',
    label: 'Material starts moving',
    short: 'Material',
    tone: 'alt',
    stage: 'material',
    lede: 'Cement is ordered, received, issued to a site and billed — four events that normally live in four places. Here each one is linked to the one before it, so a bill nobody ordered stops being possible.',
    outcome: 'Stock valued from real movements, and every bill traceable to an order.',
    modules: ['inventory', 'purchase-orders', 'bills', 'vendors'],
    videos: [
      { id: 'W1bLwRo2qJ0', title: 'Materials Management', blurb: 'Goods received, issued and valued at a moving average across your stores.', published: '2026-09-13' },
      { id: 'vCIDX5YgC1E', title: 'Manage Site Materials', blurb: 'What a site has drawn, what it was charged, and what came back.', published: '2026-09-13' },
      { id: 'ifzNcnWBIqI', title: 'Create & Manage Vendors', blurb: 'One record per supplier — GSTIN, terms, bill history and what you still owe.', published: '2026-09-13' },
      { id: 'GjIspz5mPUc', title: 'Vendor Bills & Payments', blurb: 'Recording a bill with GST and TDS, verifying it, and paying it down.', published: '2026-09-13' },
    ],
  },
  {
    n: '06',
    slug: 'contractors',
    label: 'Contractors do the work',
    short: 'Contractors',
    tone: 'light',
    stage: 'material',
    lede: 'Most of the work on most sites is done by somebody else. A contractor gets a work order with a scope and a rate, and is paid against what was certified — not against what they remember agreeing.',
    outcome: 'Every payout sits against a work order, on a site, in the books.',
    modules: ['vendors', 'ledger'],
    videos: [
      { id: 'Nz9X8f7yBZo', title: 'Create & Manage Contractors', blurb: 'Setting up a contractor, their trade and their commercial terms.', published: '2026-09-14' },
      { id: 'HWLftPePqSU', title: 'Work Orders', blurb: 'Issuing a work order with scope and rate, and tracking it to completion.', published: '2026-09-14' },
      { id: 'A8PJWrX8RNc', title: 'Manage Site Contractors', blurb: 'Who is working on this site, against which order, at what value.', published: '2026-09-13' },
      { id: '3yCKE6W7mSo', title: 'Contractor Payouts & Payments', blurb: 'Certifying and paying a contractor, posted against the right site.', published: '2026-09-14' },
    ],
  },
  {
    n: '07',
    slug: 'wages',
    label: 'The crew gets paid',
    short: 'Payroll',
    tone: 'alt',
    stage: 'wages',
    lede: 'Mason, helper, maistry. The muster marked at the gate is what payroll runs on — so labour cost lands on the site that used it, and an advance taken in the second week is already accounted for on payday.',
    outcome: 'A payroll run built from real attendance, with advances and settlements squared.',
    modules: ['employees', 'attendance', 'payroll'],
    videos: [
      { id: 'WHS2k5ibF58', title: 'Employee Management', blurb: 'Office staff and site crews, with departments, designations and postings.', published: '2026-09-13' },
      { id: '7AC1ySw35v0', title: 'Employee Attendance', blurb: 'The attendance record behind every pay line, including leave and LOP.', published: '2026-09-13' },
      { id: 'yH6BWU2v9Vk', title: 'Earnings & Salary Management', blurb: 'Setting what a person earns — daily wage, monthly salary, overtime and allowances.', published: '2026-09-13' },
      { id: 'PU2F0B8o_Jc', title: 'Employee Advances', blurb: 'Recording an advance and recovering it without a side register.', published: '2026-09-14' },
      { id: 'oRWwcnIvWfo', title: 'Labour Settlement', blurb: 'Settling temporary labour — what is owed, paid and closed out.', published: '2026-09-13' },
      { id: '9SXw6MzaYHY', title: 'Process Employee Payroll', blurb: 'Running the month: preview, adjust, post to the ledger against each site.', published: '2026-09-13' },
    ],
  },
  {
    n: '08',
    slug: 'money',
    label: 'You see where the money went',
    short: 'Money',
    tone: 'light',
    stage: 'money',
    lede: 'Every entry above has already written itself into the books, which is why the profit on a plot is a number you can look at on a Tuesday rather than something your auditor tells you in September. Who in your office may look at it is yours to decide, per role, per person.',
    outcome: 'Per-site cost and profit you can act on, visible only to the people you choose.',
    modules: ['ledger', 'reports'],
    videos: [
      { id: '5vGBJnFJYQg', title: 'Site Financials & Project Costs', blurb: 'What a site has cost, what has been received, and where it stands against budget.', published: '2026-09-13' },
      { id: 'jSAeCkVMQlQ', title: 'Admin — User Management', blurb: 'Adding users and setting who can see money, sites and people.', published: '2026-09-14' },
    ],
  },
];

/** Flat list, in page order — used for the VideoObject schema. */
export const ALL_VIDEOS: Video[] = HOW_IT_WORKS_CHAPTERS.flatMap((c) => c.videos);

/**
 * maxresdefault is the true 16:9 frame (1280x720); hqdefault is 4:3 with black
 * bars baked in, which is why it is only the fallback. Every one of the 26 was
 * checked to have a maxres frame, but YouTube does not guarantee one forever,
 * so VideoEmbed swaps to hq on error.
 */
export const thumbnailUrl = (id: string) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
export const thumbnailFallbackUrl = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
export const embedUrl = (id: string) => `https://www.youtube-nocookie.com/embed/${id}`;
export const watchUrl = (id: string) => `https://www.youtube.com/watch?v=${id}`;

/**
 * Development-time registry validation.
 *
 * Twenty-six video ids are typed by hand here, and a single wrong character
 * produces a blank thumbnail that nobody notices until a prospect sees it. The
 * project defers test files, so this is the cheapest thing that still fails
 * loudly — it runs on import in dev and is stripped from the production bundle.
 */
if (process.env.NODE_ENV !== 'production') {
  const ids = ALL_VIDEOS.map((v) => v.id);
  if (ALL_VIDEOS.length !== 26) throw new Error(`howItWorksVideos: expected 26 videos, got ${ALL_VIDEOS.length}`);
  if (new Set(ids).size !== ids.length) throw new Error('howItWorksVideos: duplicate YouTube video id');
  if (!ids.every((id) => /^[A-Za-z0-9_-]{11}$/.test(id))) throw new Error('howItWorksVideos: malformed YouTube video id');
  if (ALL_VIDEOS.some((v) => !v.title || !v.blurb || !/^\d{4}-\d{2}-\d{2}$/.test(v.published))) {
    throw new Error('howItWorksVideos: a video row is missing a title, blurb or real published date');
  }
  const slugs = HOW_IT_WORKS_CHAPTERS.map((c) => c.slug);
  if (new Set(slugs).size !== slugs.length) throw new Error('howItWorksVideos: duplicate chapter slug');
  if (HOW_IT_WORKS_CHAPTERS.some((c) => c.videos.length === 0)) throw new Error('howItWorksVideos: a chapter has no videos');
  const known = new Set(MARKETING_MODULES.map((m) => m.slug));
  HOW_IT_WORKS_CHAPTERS.flatMap((c) => c.modules).forEach((s) => {
    if (!known.has(s)) throw new Error(`howItWorksVideos: "${s}" is not a MARKETING_MODULES slug`);
  });
}
